"use client";

import { Icon, ospThemes, deviceDetector } from "@ospweb/sdk-ui";
import Image from "next/image";
import { AppImage } from "@/src/components/NextImage";
import { useEffect, useState } from "react";
import { useMemoizedFn } from "ahooks";
import log from "logbeacon";
import Translations from "@translate/components/PWA/PwdDialog";
import { I18nUtil } from "@utils";

const I18n = I18nUtil.createScoped(Translations);

const PWA_DIALOG_LAST_SHOWN_KEY = "pwa-dialog-last-shown";
const SHOW_INTERVAL =
  Number(process.env.NEXT_PUBLIC_PWA_DIALOG_SHOW_INTERVAL || "") ||
  24 * 60 * 60 * 1000; // 24 hours in milliseconds

function DialogTitle({ onClose }: { onClose: () => void }) {
  return (
    <div className="flex flex-row items-center justify-between w-full mt-8 px-8">
      <Icon
        data-openreplay-label="close"
        name="close"
        size={16}
        onClick={onClose}
        color={ospThemes.colors.content_primary}
      />
      <div className="flex items-center gap-2">
        <picture>
          <source
            srcSet="/edgen_logo_dark.svg"
            type="image/svg+xml"
            media="(min-width: 960px)"
          />
          <Image src="/edgen_logo_light.svg" alt="" width={114} height={32} />
        </picture>
      </div>
      <div />
    </div>
  );
}

export function PwdDialog() {
  const [show, setShow] = useState(false);

  // 检查是否应该显示对话框
  const shouldShowDialog = useMemoizedFn(() => {
    try {
      // 如果不是移动设备，不显示
      if (!deviceDetector.isMobile()) {
        return false;
      }

      if (deviceDetector.checkIfPWA()) {
        return false;
      }

      const lastShown = localStorage.getItem(PWA_DIALOG_LAST_SHOWN_KEY);
      if (!lastShown) {
        return true;
      }

      const lastShownTime = parseInt(lastShown, 10);
      if (isNaN(lastShownTime)) {
        return true;
      }

      return Date.now() - lastShownTime > SHOW_INTERVAL;
    } catch (error) {
      log.error("[文案]Error checking PWA dialog state:", error);
      return false; // 发生错误时不显示对话框
    }
  });

  const handleChange = useMemoizedFn((e: MediaQueryListEvent) => {
    if (e.matches) {
      setShow(false);
    }
  });

  useEffect(() => {
    // 检查初始状态
    setShow(shouldShowDialog());

    // 监听 PWA 安装状态变化
    const mediaQuery = window.matchMedia("(display-mode: standalone)");
    mediaQuery.addEventListener("change", handleChange);

    return () => {
      mediaQuery.removeEventListener("change", handleChange);
    };
  }, []);

  // 监听 show 状态变化，控制页面滚动和下拉刷新
  useEffect(() => {
    const preventDefault = (e: TouchEvent) => {
      e.preventDefault();
    };

    if (show) {
      // 保存当前滚动位置
      const scrollY = window.scrollY;
      // 禁用滚动
      document.body.style.position = "fixed";
      document.body.style.width = "100%";
      document.body.style.top = `-${scrollY}px`;
      // 禁用下拉刷新
      document.body.addEventListener("touchmove", preventDefault, {
        passive: false,
      });
    } else {
      // 恢复滚动
      const scrollY = document.body.style.top;
      document.body.style.position = "";
      document.body.style.width = "";
      document.body.style.top = "";
      window.scrollTo(0, parseInt(scrollY || "0") * -1);
      // 移除下拉刷新限制
      document.body.removeEventListener("touchmove", preventDefault);
    }

    return () => {
      // 组件卸载时确保恢复滚动和移除事件监听
      document.body.style.position = "";
      document.body.style.width = "";
      document.body.style.top = "";
      document.body.removeEventListener("touchmove", preventDefault);
    };
  }, [show]);

  const handleClose = useMemoizedFn(() => {
    log.debug("[文案]handleClose");
    setShow(false);
    try {
      localStorage.setItem(PWA_DIALOG_LAST_SHOWN_KEY, Date.now().toString());
    } catch (error) {
      log.error("[文案]Error saving PWA dialog state:", error);
    }
  });

  if (!show) {
    return null;
  }

  return (
    <div className="flex flex-col items-center text-center w-full absolute h-vh z-[9999]">
      <AppImage
        src="/assets/pwd_pop_bg.png"
        alt=""
        className="h-vh w-full absolute z-[-1] object-cover"
        width={300}
        height={300}
      />
      <DialogTitle onClose={handleClose} />
      <AppImage
        src="/assets/pwd_home.png"
        alt="Add to Home Screen"
        width={101}
        height={103}
        className="mt-50"
      />
      <h1
        aria-label="Pwd Dialog h1"
        data-openreplay-label="Pwd Dialog h1 Semantic"
        id="sem-pwd-dialog-h1"
        className="headline_regular_m text-content_primary mb-4"
      >
        {I18n.t("Add to Home Screen")}
      </h1>
      <p className="body_m text-content_secondary mb-5">
        {I18n.t("To install the app, you need to add this website to")}
        <br />
        {I18n.t("your home screen.")}
      </p>
      <p className="body_m text-content_secondary">
        {I18n.t(
          "In your Safari browser menu, tap the%{var0} icon and choose in the options",
          {
            var0: " ",
          },
        )}
      </p>
    </div>
  );
}
